import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, Minus, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  groupCheckState,
  toggleGroup,
  type FilterGroup,
  type GroupCheckState,
} from "@/utils/employeeGroups";

type Employee = {
  id: string;
  name: string;
  phone: string;
  status: "active" | "inactive";
  email?: string;
};

type EmployeeAssignmentProps = {
  assignedEmployees?: string[];
  availableEmployees: Employee[];
  /**
   * Saved grupos offered as presets at the top of the picker. Members are
   * narrowed to the assignable roster here, so a group carrying an inactive
   * asesor can never smuggle them into a rule.
   */
  groups?: FilterGroup[];
  onAddEmployees: (employeeIds: string[]) => void;
  onRemoveEmployees: (employeeIds: string[]) => void;
  /**
   * Runs against the membership a change would produce, so the same rule covers
   * removing one chip and saving a whole picker session. Returning a message
   * blocks the change and shows it; returning null allows it.
   */
  validateChange?: (nextEmployeeIds: string[]) => string | null;
};

// Below this a roster is faster to scan than to type into — same threshold the
// Tablero's asesor filter uses, so the two surfaces behave alike.
const SEARCHABLE_THRESHOLD = 8;

// A rule at the bigger merchants holds 100+ asesores. Past this the chips stop
// being a roster you read and become a wall you scroll, so the rest collapse
// behind a count the merchant can open on demand.
const CHIP_COLLAPSE_LIMIT = 10;

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const byName = (a: Employee, b: Employee) => a.name.localeCompare(b.name, "es");

const matchEmployees = (employees: Employee[], query: string) => {
  const needle = normalize(query.trim());
  if (!needle) return employees;
  return employees.filter(
    (employee) =>
      normalize(employee.name).includes(needle) ||
      (employee.email ? normalize(employee.email).includes(needle) : false)
  );
};

// A group also matches on its members, so typing an asesor's name surfaces the
// groups they belong to alongside their own row.
const matchGroups = (groups: FilterGroup[], employees: Employee[], query: string) => {
  const needle = normalize(query.trim());
  if (!needle) return groups;
  const nameById = new Map(employees.map((employee) => [employee.id, employee.name]));
  return groups.filter(
    (group) =>
      normalize(group.label).includes(needle) ||
      group.employeeIds.some((id) => normalize(nameById.get(id) ?? "").includes(needle))
  );
};

const checkStateOf = (ids: string[], selected: string[]): GroupCheckState => {
  if (!ids.length) return "none";
  const hits = ids.filter((id) => selected.includes(id)).length;
  if (hits === 0) return "none";
  return hits === ids.length ? "all" : "some";
};

// Screen readers get the check state spoken, since `aria-selected` on an option
// is a boolean and cannot express the half-selected group.
const stateSuffix: Record<GroupCheckState, string> = {
  all: "seleccionado",
  some: "seleccionado en parte",
  none: "sin seleccionar",
};

type PickerItem = {
  key: string;
  kind: "all" | "group" | "employee";
  label: string;
  meta?: string;
  state: GroupCheckState;
  navigable: boolean;
  onToggle: () => void;
};

const chipBase =
  "inline-flex min-h-11 items-center gap-1.5 rounded-pill px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";

// aria-hidden because a listbox may only own options — the grouping is visual,
// and each row already speaks its own count and check state.
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p aria-hidden className="px-2 pb-1 pt-3 text-xs font-medium text-muted-foreground">
    {children}
  </p>
);

const PickerRow = ({
  id,
  item,
  active,
  interactive,
  onActivate,
}: {
  id: string;
  item: PickerItem;
  active: boolean;
  /** True while the search box owns focus and drives the list with the arrows. */
  interactive: boolean;
  onActivate: () => void;
}) => (
  <button
    type="button"
    id={id}
    role="option"
    aria-selected={item.state === "all"}
    aria-label={`${item.label}${item.meta ? `, ${item.meta}` : ""}, ${stateSuffix[item.state]}`}
    aria-disabled={!item.navigable}
    disabled={!item.navigable}
    data-active={active || undefined}
    tabIndex={interactive ? -1 : 0}
    onClick={item.onToggle}
    onMouseMove={onActivate}
    className={cn(
      "flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      !item.navigable && "cursor-not-allowed text-muted-foreground",
      item.navigable && item.state !== "none" && "text-palomma-primary",
      item.navigable && active && "bg-accent"
    )}
  >
    <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
      {item.state === "all" ? (
        <Check className="size-4" />
      ) : item.state === "some" ? (
        <Minus className="size-4" />
      ) : null}
    </span>
    <span className="flex-1 truncate" aria-hidden>
      {item.label}
    </span>
    {item.meta ? (
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground" aria-hidden>
        {item.meta}
      </span>
    ) : null}
  </button>
);

/**
 * Asesores on an assignment rule, its fallback, or a grupo. The roster runs to
 * ~120 people at the bigger merchants, so adding them is a staged multi-select —
 * search, grupos as presets, select-all — committed as ONE add and ONE remove
 * call. Both routers already take `employeeIds` arrays; the chip wall this
 * replaced fired a mutation and a refetch per person and reflowed under the
 * cursor after every one.
 *
 * The trigger is a chip at the end of the chip row rather than a button off to
 * the side: it puts "add" where the eye already is, and keeps the surface to one
 * purple (DESIGN.md — purple marks the action, it never decorates).
 *
 * Once the roster is searchable the picker is a combobox, not a list of tab
 * stops: focus stays in the search box, the arrows move a highlight, Enter
 * toggles it, and Tab reaches Guardar in one press instead of 113.
 */
export const EmployeeAssignment = ({
  assignedEmployees,
  availableEmployees,
  groups,
  onAddEmployees,
  onRemoveEmployees,
  validateChange,
}: EmployeeAssignmentProps) => {
  const listboxId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showAllChips, setShowAllChips] = useState(false);

  const assigned = useMemo(() => assignedEmployees ?? [], [assignedEmployees]);

  // Only active asesores can be picked; an already-assigned inactive one keeps
  // its chip (and its place in `selected`) so saving never quietly drops them.
  const assignable = useMemo(
    () => availableEmployees.filter((employee) => employee.status === "active").sort(byName),
    [availableEmployees]
  );

  const assignedChips = useMemo(
    () =>
      assigned
        .map((id) => availableEmployees.find((employee) => employee.id === id))
        .filter((employee): employee is Employee => !!employee)
        .sort(byName),
    [assigned, availableEmployees]
  );

  const presetGroups = useMemo(() => {
    if (!groups?.length) return [];
    const assignableIds = new Set(assignable.map((employee) => employee.id));
    return groups
      .map((group) => ({
        ...group,
        employeeIds: group.employeeIds.filter((id) => assignableIds.has(id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [groups, assignable]);

  const searchable = assignable.length + presetGroups.length > SEARCHABLE_THRESHOLD;
  const trimmedQuery = query.trim();
  const visible = searchable ? matchEmployees(assignable, query) : assignable;
  const visibleGroups = searchable ? matchGroups(presetGroups, assignable, query) : presetGroups;

  const toAdd = selected.filter((id) => !assigned.includes(id));
  const toRemove = assigned.filter((id) => !selected.includes(id));
  const isDirty = toAdd.length > 0 || toRemove.length > 0;

  const overflowCount = Math.max(assignedChips.length - CHIP_COLLAPSE_LIMIT, 0);
  const shownChips =
    overflowCount && !showAllChips ? assignedChips.slice(0, CHIP_COLLAPSE_LIMIT) : assignedChips;

  const handleToggle = (employeeId: string) =>
    setSelected((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );

  const handleToggleGroup = (group: FilterGroup) =>
    setSelected((prev) => toggleGroup(group, prev));

  // "Todos" follows the search: with a query typed it covers exactly the
  // matches, which is how "agregá a todos los de Medellín" stays one click.
  const handleToggleAllVisible = () =>
    setSelected((prev) => {
      const visibleIds = visible.map((employee) => employee.id);
      return checkStateOf(visibleIds, prev) === "all"
        ? prev.filter((id) => !visibleIds.includes(id))
        : [...new Set([...prev, ...visibleIds])];
    });

  // One flat list drives both rendering and arrow navigation, so an index can
  // never point at a different row than the one the merchant sees highlighted.
  const items = useMemo<PickerItem[]>(() => {
    const rows: PickerItem[] = [];
    if (visible.length) {
      rows.push({
        key: "all",
        kind: "all",
        label: trimmedQuery ? "Todos los resultados" : "Todos los asesores",
        meta: `${visible.length}`,
        state: checkStateOf(
          visible.map((employee) => employee.id),
          selected
        ),
        navigable: true,
        onToggle: handleToggleAllVisible,
      });
    }
    visibleGroups.forEach((group) =>
      rows.push({
        key: `g:${group.id}`,
        kind: "group",
        label: group.label,
        meta: group.employeeIds.length ? `${group.employeeIds.length}` : "sin asesores",
        state: groupCheckState(group, selected),
        navigable: group.employeeIds.length > 0,
        onToggle: () => handleToggleGroup(group),
      })
    );
    visible.forEach((employee) =>
      rows.push({
        key: `e:${employee.id}`,
        kind: "employee",
        label: employee.name,
        state: selected.includes(employee.id) ? "all" : "none",
        navigable: true,
        onToggle: () => handleToggle(employee.id),
      })
    );
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, visibleGroups, selected, trimmedQuery]);

  const firstNavigable = items.findIndex((item) => item.navigable);

  // A new query rebuilds the list under the highlight, so it goes back to the
  // top match — which is the row Enter should hit after typing a name.
  useEffect(() => setActiveIndex(firstNavigable), [trimmedQuery, firstNavigable]);

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  // Radix moves focus onto the popover content when it opens; the search box
  // claims it right after that lands, so the picker is type-ready without a
  // click. Mirrors the Tablero filter and the chat EmployeePicker.
  useEffect(() => {
    if (!open || !searchable) return;
    const id = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open, searchable]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    setQuery("");
    if (next) setSelected(assigned);
  };

  const moveActive = (delta: number) => {
    if (!items.length) return;
    let next = activeIndex;
    for (let step = 0; step < items.length; step += 1) {
      next = (next + delta + items.length) % items.length;
      if (items[next]?.navigable) break;
    }
    setActiveIndex(next);
  };

  // Enter toggles the highlighted row and clears the query, so several asesores
  // can be stacked up without leaving the keyboard.
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (event.key !== "Enter") return;
    event.preventDefault();
    const item = items[activeIndex];
    if (!item?.navigable) return;
    item.onToggle();
    setQuery("");
  };

  const handleSave = () => {
    const error = validateChange?.(selected);
    if (error) {
      toast.error(error);
      return;
    }
    // Adds go first so a rule that must keep someone assigned is never
    // momentarily empty mid-save.
    if (toAdd.length) onAddEmployees(toAdd);
    if (toRemove.length) onRemoveEmployees(toRemove);
    handleOpenChange(false);
  };

  const handleRemoveChip = (employeeId: string) => {
    const error = validateChange?.(assigned.filter((id) => id !== employeeId));
    if (error) {
      toast.error(error);
      return;
    }
    onRemoveEmployees([employeeId]);
  };

  const rowId = (index: number) => `${listboxId}-row-${index}`;
  const activeItem = items[activeIndex];

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">
        {assigned.length ? `Asesores asignados (${assigned.length})` : "Sin asesores asignados"}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {shownChips.map((employee) => (
          <button
            key={employee.id}
            type="button"
            onClick={() => handleRemoveChip(employee.id)}
            aria-label={`Quitar a ${employee.name}`}
            className={cn(
              chipBase,
              "border border-palomma-primary/20 bg-palomma-primary/10 text-palomma-primary hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            )}
          >
            <span>{employee.name}</span>
            <X className="size-3.5" aria-hidden />
          </button>
        ))}

        {overflowCount ? (
          <button
            type="button"
            onClick={() => setShowAllChips((prev) => !prev)}
            className={cn(chipBase, "bg-muted text-foreground hover:bg-gray-primary")}
          >
            {showAllChips ? "Ver menos" : `+${overflowCount} más`}
          </button>
        ) : null}

        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                chipBase,
                "border border-dashed border-input text-muted-foreground",
                "hover:border-palomma-primary hover:bg-palomma-primary/5 hover:text-palomma-primary",
                "data-[state=open]:border-palomma-primary data-[state=open]:bg-palomma-primary/5 data-[state=open]:text-palomma-primary"
              )}
            >
              <Plus className="size-3.5" aria-hidden />
              <span>{assigned.length ? "Agregar" : "Agregar asesores"}</span>
            </button>
          </PopoverTrigger>

          <PopoverContent align="start" className="w-[min(20rem,calc(100vw-2rem))] p-0">
            {searchable ? (
              <div className="relative border-b border-border p-2">
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <input
                  ref={searchRef}
                  value={query}
                  role="combobox"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-autocomplete="list"
                  aria-activedescendant={activeItem?.navigable ? rowId(activeIndex) : undefined}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Buscar asesor…"
                  aria-label="Buscar asesor"
                  className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-2 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring md:text-sm"
                />
              </div>
            ) : null}

            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-multiselectable
              aria-label="Asesores"
              className="max-h-[min(50vh,380px)] overflow-y-auto p-1"
            >
              {items.map((item, index) => (
                <Fragment key={item.key}>
                  {item.kind === "group" && items[index - 1]?.kind !== "group" ? (
                    <SectionLabel>Grupos</SectionLabel>
                  ) : null}
                  {item.kind === "employee" && items[index - 1]?.kind === "group" ? (
                    <SectionLabel>Asesores</SectionLabel>
                  ) : null}
                  <PickerRow
                    id={rowId(index)}
                    item={item}
                    active={index === activeIndex}
                    interactive={searchable}
                    onActivate={() => item.navigable && setActiveIndex(index)}
                  />
                  {item.kind === "all" ? (
                    <div className="my-1 h-px bg-border" role="none" />
                  ) : null}
                </Fragment>
              ))}

              {items.length === 0 ? (
                <p className="px-2 py-8 text-center text-sm text-muted-foreground">
                  {trimmedQuery
                    ? `Sin coincidencias para «${trimmedQuery}»`
                    : "No hay asesores activos para asignar."}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border p-2">
              <span className="text-xs text-muted-foreground" aria-live="polite">
                {isDirty
                  ? [
                      toAdd.length ? `${toAdd.length} por agregar` : null,
                      toRemove.length ? `${toRemove.length} por quitar` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "Sin cambios"}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
                  Cancelar
                </Button>
                <Button size="sm" disabled={!isDirty} onClick={handleSave}>
                  Guardar
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
